import React, { useCallback, useEffect, useState, useRef, lazy, Component } from 'react';
import { scanProducts, pickRandomProduct, type ScannedProduct } from '../utils/productScanner';
// --- Data lists ---
const NAMES = ['María G.', 'Carlos R.', 'Ana L.', 'Jorge M.', 'Lucía P.', 'Diego S.', 'Valentina F.', 'Andrés T.', 'Camila H.', 'Sebastián V.', 'Isabella D.', 'Mateo C.', 'Sofía B.', 'Daniel A.', 'Paula N.', 'Alejandro K.', 'Gabriela W.', 'Fernando J.', 'Catalina E.', 'Ricardo O.', 'Laura M.', 'Miguel Á.', 'Natalia R.', 'Tomás P.', 'Mariana S.', 'Emilia V.', 'Santiago L.', 'Florencia D.', 'Nicolás B.', 'Julieta C.', 'Roberto H.', 'Carolina T.', 'Martín F.', 'Renata G.', 'Eduardo A.'];
const CITIES = ['Miami', 'Houston', 'Los Angeles', 'New York', 'Chicago', 'Phoenix', 'San Antonio', 'Dallas', 'San Diego', 'Austin', 'Denver', 'Orlando', 'Tampa', 'Atlanta', 'Las Vegas', 'Charlotte', 'Portland', 'Seattle', 'Nashville', 'San Francisco', 'Sacramento', 'Tucson', 'El Paso', 'Albuquerque', 'Raleigh'];
// --- Types ---
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
// --- Helpers ---
function pickUniqueName(recentNames: string[]): string {
  const available = NAMES.filter(n => !recentNames.includes(n));
  const pool = available.length > 0 ? available : NAMES;
  return pool[Math.floor(Math.random() * pool.length)];
}
function pickCity(): string {
  return CITIES[Math.floor(Math.random() * CITIES.length)];
}
function randomInterval(): number {
  return 8000 + Math.random() * 7000; // 8–15 seconds
}
// --- Component ---
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [phase, setPhase] = useState<'entering' | 'visible' | 'exiting' | 'hidden'>('hidden');
  const productsRef = useRef<ScannedProduct[]>([]);
  const recentNamesRef = useRef<string[]>([]);
  const counterRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prefersReducedMotion = useRef(false);
  // Check reduced motion preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    const handler = (e: MediaQueryListEvent) => {
      prefersReducedMotion.current = e.matches;
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const showToast = useCallback(() => {
    const name = pickUniqueName(recentNamesRef.current);
    // Update rolling history (keep last 10)
    recentNamesRef.current = [...recentNamesRef.current, name].slice(-10);
    const city = pickCity();
    const product = pickRandomProduct(productsRef.current);
    counterRef.current += 1;
    const newToast: ToastData = {
      id: counterRef.current,
      name,
      city,
      product
    };
    setToast(newToast);
    setPhase('entering');
    // After enter animation, set to visible
    setTimeout(() => setPhase('visible'), 50);
    // After 4s visible, start exit
    setTimeout(() => {
      setPhase('exiting');
      // After exit animation, hide and schedule next
      setTimeout(() => {
        setPhase('hidden');
        setToast(null);
        scheduleNext();
      }, prefersReducedMotion.current ? 50 : 400);
    }, 4000);
  }, []);
  const scheduleNext = useCallback(() => {
    timerRef.current = setTimeout(() => {
      showToast();
    }, randomInterval());
  }, [showToast]);
  // Initialize: scan products, start first toast after 3s
  useEffect(() => {
    // Small delay to let DOM render before scanning
    const initTimer = setTimeout(() => {
      productsRef.current = scanProducts();
    }, 500);
    const firstTimer = setTimeout(() => {
      showToast();
    }, 3000);
    return () => {
      clearTimeout(initTimer);
      clearTimeout(firstTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showToast]);
  if (phase === 'hidden' || !toast) return null;
  const reduced = prefersReducedMotion.current;
  const animationStyles: React.CSSProperties = reduced ? {
    opacity: phase === 'exiting' ? 0 : 1
  } : {
    transform: phase === 'entering' ? 'translateY(100%)' : phase === 'exiting' ? 'translateY(100%)' : 'translateY(0)',
    opacity: phase === 'exiting' ? 0 : phase === 'entering' ? 0 : 1,
    transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease'
  };
  return <div className="fixed bottom-6 left-1/2 z-[60] pointer-events-none" style={{
    transform: 'translateX(-50%)'
  }} aria-live="polite" role="status" data-id="element-461">
      <div className="pointer-events-auto bg-white rounded-xl shadow-lg border border-amber-100 px-4 py-3 flex items-center gap-3 max-w-sm w-[calc(100vw-2rem)]" style={animationStyles} data-id="element-462">
        {/* Thumbnail */}
        <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-amber-50" data-id="element-463">
          <img src={toast.product.thumbnailUrl} alt="" className="w-full h-full object-cover" loading="lazy" data-id="element-464" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-465">
          <p className="text-sm text-gray-800 leading-snug" data-id="element-466">
            <span className="font-semibold text-gray-900" data-id="element-467">{toast.name}</span>
            {' en '}
            <span className="text-gray-600" data-id="element-468">{toast.city}</span>
            {' acaba de comprar'}
          </p>
          <p className="text-sm font-medium text-amber-700 truncate mt-0.5" data-id="element-469">
            {toast.product.title}
            {toast.product.editionLabel ? ` — ${toast.product.editionLabel}` : ''}
            {' — '}
            <span className="font-bold" data-id="element-470">{toast.product.priceText}</span>
          </p>
        </div>

        {/* Subtle pulse dot */}
        <div className="flex-shrink-0" data-id="element-471">
          <span className="relative flex h-2.5 w-2.5" data-id="element-472">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-473" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" data-id="element-474" />
          </span>
        </div>
      </div>
    </div>;
}